import {
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  Link,
  Text,
  VStack
} from '@chakra-ui/react'
import { SymptomTracker } from '@components/icons'
import { SendIcon } from '@icons'
import React from 'react'

interface RewardNotificationTabProps {
  freeMonths?: number
  onTermsClick?: () => void
  onSendInvitesClick?: () => void
  onMonitorStatusClick?: () => void
}

const RewardNotificationTab: React.FC<RewardNotificationTabProps> = ({
  freeMonths = 0,
  onTermsClick,
  onSendInvitesClick,
  onMonitorStatusClick
}) => {
  const hasRewards = freeMonths > 0

  if (!hasRewards) {
    return (
      <Box
        p={6}
        maxH="460px"
        overflowY="auto"
        position="relative"
        className="reward-notification-container"
      >
        <VStack spacing={6} align="center" textAlign="center" py={10}>
          <Heading as="h2" fontSize="24px" fontWeight="bold">
            You haven't earned any rewards yet
          </Heading>

          <Text
            fontSize="md"
            color="gray.700"
            maxW="400px"
            mt="12px !important"
          >
            Don't worry, it's a numbers game — the more invites you send, the
            more likely you'll save big time!
          </Text>

          <VStack spacing={3} width="220px" mt={4}>
            <Button
              as={Link}
              leftIcon={<SendIcon fill="#2D54E9" />}
              width="100%"
              variant="outline"
              borderColor="rgba(55, 60, 65, 0.14)"
              colorScheme="#2D54E9"
              bg="rgba(45, 84, 232, 0.10)"
              color="#2D54E9"
              borderRadius="full"
              onClick={onSendInvitesClick}
              _hover={{
                textDecoration: 'none',
                bg: 'rgba(45, 84, 232, 0.15)'
              }}
            >
              Send more invites
            </Button>

            <Button
              as={Link}
              leftIcon={<SymptomTracker fill="#2D54E9" size={16} />}
              width="100%"
              variant="outline"
              borderColor="rgba(55, 60, 65, 0.14)"
              colorScheme="#2D54E9"
              bg="rgba(45, 84, 232, 0.10)"
              color="#2D54E9"
              borderRadius="full"
              onClick={onMonitorStatusClick}
              _hover={{
                textDecoration: 'none',
                bg: 'rgba(45, 84, 232, 0.15)'
              }}
            >
              Monitor invite status
            </Button>
          </VStack>
        </VStack>
      </Box>
    )
  }

  return (
    <Box
      p={6}
      maxH="460px"
      overflowY="auto"
      position="relative"
      className="reward-notification-container"
    >
      <VStack spacing={6} align="center" textAlign="center">
        <Flex>
          <Text fontSize="24px">🥳</Text>
          <Text fontSize="24px" ml={2}>
            🎉
          </Text>
        </Flex>

        <Heading as="h2" fontSize="24px" fontWeight="bold" mt="8px !important">
          You earned
          <Text as="span" color="primary" fontSize="24px">
            {' '}
            {freeMonths} FREE months*
          </Text>
          <br />
          of Blueprint
        </Heading>

        <Text fontSize="md" color="gray.700" maxW="400px">
          This reward will automatically be applied at the start of your next
          billing cycle.
        </Text>

        <Flex w="100%" alignItems="center" my={2}>
          <Divider />
          <Text px={4} color="#282828" fontSize="12px">
            or
          </Text>
          <Divider />
        </Flex>

        <Box
          w="100%"
          bg="gray.50"
          border="1px solid #E4E5E6"
          p={6}
          borderRadius="6px"
          textAlign="left"
        >
          <Text fontWeight="bold" fontSize="12px">
            Maximize your savings
          </Text>
          <Text fontSize="12px" color="gray.700" mb={4}>
            You're on a 60 session plan currently. To maximize your savings and
            begin your free use of Blueprint now, upgrade your plan.
          </Text>
          <Button
            w="100%"
            colorScheme="blue"
            bg="#2D54E9"
            size="lg"
            borderRadius="full"
            as={Link}
            href="/settings/update-your-plan"
            target="_blank"
            _hover={{
              textDecoration: 'none'
            }}
          >
            Update plan
          </Button>
        </Box>

        <VStack spacing={1} mt={2} w="100%" align="flex-start">
          <Text fontSize="xs" color="#282828">
            * 1 free month = 120 free sessions max
          </Text>
          <Text fontSize="xs" color="#282828">
            * View{' '}
            <Link color="blue.500" onClick={onTermsClick} cursor="pointer">
              Terms
            </Link>{' '}
            for details
          </Text>
        </VStack>
      </VStack>
    </Box>
  )
}

export default RewardNotificationTab
